import { Point2D as Point2DInterface } from '@paragon/contract-library';
import { distanceGreaterThanOrEqual, distanceLessThanOrEqual, Point2D } from '@paragon/geometry-library';
import { assertExhaustiveSwitch, roundToIncrement } from '@paragon/utilities';

import { AxisRange, AxisSnapTarget, SnapAxis } from './types';

function snapToSixteenth(v: number): number {
  return roundToIncrement(v, 1 / 16);
}

export function roundPoint2D(point: Point2D): Point2D {
  return new Point2D(snapToSixteenth(point.x), snapToSixteenth(point.y));
}

export function getAxisValue(point: Point2DInterface, axis: SnapAxis): number {
  switch (axis) {
    case 'x':
      return point.x;

    case 'y':
      return point.y;

    default:
      assertExhaustiveSwitch(axis);
  }
}

export function getPerpendicularAxis(axis: SnapAxis): SnapAxis {
  switch (axis) {
    case 'x':
      return 'y';

    case 'y':
      return 'x';

    default:
      assertExhaustiveSwitch(axis);
  }
}

export interface AxisSnapResult {
  readonly axisTarget: number;
  readonly snapLocation: Point2D;
}

/**
 * Resolves how snapping `sourceCorner` to `target` would play out:
 * - `axisTarget` is the target coordinate along `target.axis`.
 * - `snapLocation` is the 2D point produced by the snap, used by candidate ranking to prefer spatially nearby targets
 *     over distant ones even when on-axis distances match.
 *
 * Returns `null` when the source point falls outside the target's perpendicular bounds.
 */
export function resolveAxisSnapTarget(target: AxisSnapTarget, sourceCorner: Point2DInterface): AxisSnapResult | null {
  const perpendicularSource = getAxisValue(sourceCorner, getPerpendicularAxis(target.axis));
  const perpendicularValue = resolvePerpendicularValue(target, perpendicularSource);
  if (perpendicularValue == null) return null;

  const snapLocation =
    target.axis === 'x' ? new Point2D(target.value, perpendicularValue) : new Point2D(perpendicularValue, target.value);

  return { axisTarget: target.value, snapLocation };
}

function resolvePerpendicularValue(target: AxisSnapTarget, perpendicularSource: number): number | null {
  const perpendicular = target.perpendicular;
  switch (perpendicular.kind) {
    case 'point':
      return perpendicular.value;

    case 'range':
      return rangeContainsValue(perpendicular.range, perpendicularSource) ? perpendicularSource : null;

    case 'unbounded':
      return perpendicularSource;

    default:
      assertExhaustiveSwitch(perpendicular);
  }
}

export function resolveAxisSnapTargetPerpendicularRange(
  target: AxisSnapTarget,
  sourceCorner: Point2DInterface,
): AxisRange | null {
  const perpendicularSource = getAxisValue(sourceCorner, getPerpendicularAxis(target.axis));
  const perpendicular = target.perpendicular;

  switch (perpendicular.kind) {
    case 'point':
      return {
        lo: Math.min(perpendicular.value, perpendicularSource),
        hi: Math.max(perpendicular.value, perpendicularSource),
      };

    case 'range': {
      if (!rangeContainsValue(perpendicular.range, perpendicularSource)) return null;
      const { lo, hi } = perpendicular.range;
      return { lo: Math.min(lo, perpendicularSource), hi: Math.max(hi, perpendicularSource) };
    }

    case 'unbounded':
      return { lo: perpendicularSource, hi: perpendicularSource };

    default:
      assertExhaustiveSwitch(perpendicular);
  }
}

export function rangeContainsValue(range: AxisRange, value: number): boolean {
  return distanceGreaterThanOrEqual(value, range.lo) && distanceLessThanOrEqual(value, range.hi);
}
